import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { publish, updatePublish, reconcile, adaptedDataHelper } from 'store/schedule/schedulePageOperation';
import { SCHEDULE_DAY_VIEWS, SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import { globalBEDateFormat } from 'common/dateFormatConfig';


/**
 * Dedicated hook for schedule actions: publish, reconcile, print.
 *
 * For now we implement:
 * - publishVisible: publish all non-published, non-empty sheets for the current day;
 * - reconcileVisible: reconcile all non-reconciled sheets for the current view/day;
 * - printCurrent: placeholder to be wired to the v6 print logic.
 *
 * The goal is to centralize these behaviors so they can be reused by
 * header buttons and other components without duplicating logic.
 */
export const useScheduleActions = ({ openSnackbar } = {}) => {
  const dispatch = useDispatch();
  const sheets = useSelector((state) => state.schedule.sheets);
  const weekSheets = useSelector((state) => state.schedule.weekSheets);
  const monthSheets = useSelector((state) => state.schedule.monthSheets);
  const scheduleView = useSelector((state) => state.schedule.scheduleView);
  const scheduleType = useSelector((store) => store.schedule.scheduleType);
  const selectedDate = useSelector((state) => state.schedule.selectedDate);

  const allSheets = useMemo(() => {
    switch (scheduleView) {
      case SCHEDULE_VIEW_TYPES.monthly:
        return adaptedDataHelper(Object.values(monthSheets).flat());
      case SCHEDULE_VIEW_TYPES.weekly:
        return adaptedDataHelper(Object.values(weekSheets).flat());
      default:
        switch (scheduleType) {
          case SCHEDULE_DAY_VIEWS.grid:
            return sheets.filter(sheet => Number.isInteger(sheet.grid.position))
          default:
            return sheets;
        }
    }
  }, [sheets, monthSheets, weekSheets, scheduleView, scheduleType]);

  const publishVisible = useCallback(async (notifyWorkerIds, overrideSheetIds) => {
    let ids = overrideSheetIds;
    if (!ids) {
      if (!Array.isArray(allSheets) || !allSheets.length) return;
      ids = allSheets
        .filter((s) => !s.noWorkers)
        .filter((s) => !s.sheet.published)
        .map((s) => s.sheet._id);
    }

    if (!ids.length) return;

    await dispatch(
      publish({
        sheetIds: ids,
        notifyWorkerIds,
      })
    );
  }, [dispatch, allSheets, scheduleView]);

  const updatePublishVisible = useCallback(async (notifyWorkerIds, overrideSheetIds) => {
    let ids = overrideSheetIds;
    if (!ids) {
      if (!Array.isArray(allSheets) || !allSheets.length) return;
      ids = allSheets
        .filter((s) => !s.noWorkers)
        .filter((s) => s.sheet.published && s.sheet.unpublishedChanges)
        .map((s) => s.sheet._id);
    }

    if (!ids.length) return;

    await dispatch(
      updatePublish({
        sheetIds: ids,
        notifyWorkerIds,
      })
    );
  }, [dispatch, allSheets]);

  const hasUnreconciledSheets = useMemo(
    () => Array.isArray(allSheets) && allSheets.some((s) => !s.sheet.reconciled),
    [allSheets]
  );

  const reconcileVisible = useCallback(
    async ({ onSuccess = () => {}, onError = () => {} } = {}) => {
      if (!Array.isArray(allSheets) || !allSheets.length) return;

      const isGridView = scheduleType === SCHEDULE_DAY_VIEWS.grid;
      const dateStr = selectedDate
        ? format(new Date(selectedDate), globalBEDateFormat)
        : null;

      const sheetIds = allSheets
        .filter((s) => !s.sheet.reconciled)
        .map((s) => s.sheet._id);

      if (!sheetIds.length) return;

      await dispatch(
        reconcile({
          viewType: scheduleView,
          gridDate: isGridView && dateStr,
          date: !isGridView && selectedDate,
          sheetIds,
          onSuccess,
          onError,
        })
      );
    },
    [dispatch, allSheets, scheduleView, scheduleType, selectedDate]
  );

  return {
    publishVisible,
    updatePublishVisible,
    reconcileVisible,
    hasUnreconciledSheets,
  };
};

export default useScheduleActions;

